import React from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Grid, Box, Typography, Skeleton } from '@mui/material';

// third party
import ApexCharts from 'apexcharts';
import Chart, { Props } from 'react-apexcharts';

// project imports
import useConfig from 'hooks/useConfig';
import MainCard from 'ui-component/cards/MainCard';
import chartData from './chart-data/market-share-area-chart';

// assets
import { IconChartLine, IconCircleCheck, IconAlertTriangle } from '@tabler/icons';

// ===========================|| DASHBOARD ANALYTICS - MARKET SHARE AREA CHART CARD ||=========================== //

const IntegrationEventsAreaChartCard = ({
    series,
    totals,
    isLogs
}: {
    series: { name: string; data: any }[] | undefined;
    totals: { pSuccess: string; pError: string; sSuccess: string; sError: string; events: string; range: string } | undefined;
    isLogs?: boolean;
}) => {
    const theme = useTheme();

    const { navType } = useConfig();

    const secondaryMain = theme.palette.secondary.main;
    const errorMain = theme.palette.error.main;
    const primaryDark = theme.palette.primary.dark;
    const errorSecondary = theme.palette.warning.dark;

    React.useEffect(() => {
        const newChartData: Props = {
            ...chartData.options,
            colors: [secondaryMain, errorMain, primaryDark, errorSecondary],
            tooltip: {
                theme: navType === 'dark' ? 'dark' : 'light'
            },
            xaxis: {
                type: 'datetime',
                labels: {
                    datetimeUTC: false,
                    show: true,
                    style: {
                        colors: navType === 'dark' ? theme.palette.text.dark : theme.palette.text.secondary
                    }
                }
            },
            series
        };
        series && ApexCharts.exec(`market-share-area-chart`, 'updateOptions', newChartData);
    }, [errorMain, errorSecondary, navType, primaryDark, secondaryMain, series, theme.palette.text.dark, theme.palette.text.secondary]);

    return (
        <MainCard sx={{ '&>div': { p: 0, pb: '0px !important' } }}>
            <Box
                sx={{
                    p: 3
                }}
            >
                <Grid container direction="column" spacing={3}>
                    <Grid item container spacing={1} alignItems="center">
                        <Grid item>
                            <Typography variant="h3">Integration Events</Typography>
                        </Grid>
                        <Grid item xs zeroMinWidth />
                        <Grid item>
                            <Typography
                                sx={{
                                    width: 40,
                                    height: 40,
                                    color: theme.palette.primary.main,
                                    borderRadius: '12px',
                                    padding: 1,
                                    backgroundColor:
                                        theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.primary.light
                                }}
                            >
                                <IconChartLine stroke={2} />
                            </Typography>
                        </Grid>
                        <Grid item>
                            {totals ? (
                                <Typography variant="h3">{totals?.events?.toLocaleString() || 0}</Typography>
                            ) : (
                                <Skeleton variant="rectangular" width={100} />
                            )}
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        {totals ? (
                            <Typography sx={{ mt: -2.5, fontWeight: 400 }} color="inherit" variant="h5">
                                {totals?.range || 0}
                            </Typography>
                        ) : (
                            <Skeleton variant="rectangular" width={300} />
                        )}
                    </Grid>
                    <Grid item container justifyContent="space-around" alignItems="center" spacing={3}>
                        {isLogs ? null : (
                            <>
                                <Grid item>
                                    <Grid container alignItems="center" spacing={1}>
                                        <Grid item>
                                            <Typography
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    color: theme.palette.secondary.main,
                                                    borderRadius: '12px',
                                                    padding: 1,
                                                    backgroundColor:
                                                        theme.palette.mode === 'dark'
                                                            ? theme.palette.background.default
                                                            : theme.palette.secondary.light
                                                }}
                                            >
                                                <IconCircleCheck stroke={2} />
                                            </Typography>
                                        </Grid>
                                        <Grid item sm zeroMinWidth>
                                            {totals ? (
                                                <Typography variant="h4">
                                                    {(totals?.pSuccess !== 'NaN%' && totals?.pSuccess) || 0}
                                                </Typography>
                                            ) : (
                                                <Skeleton variant="rectangular" width={100} />
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Grid container alignItems="center" spacing={1}>
                                        <Grid item>
                                            <Typography
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    color: theme.palette.error.main,
                                                    borderRadius: '12px',
                                                    padding: 1,
                                                    backgroundColor:
                                                        theme.palette.mode === 'dark' ? theme.palette.background.default : '#ffe9e9'
                                                }}
                                            >
                                                <IconAlertTriangle stroke={2} />
                                            </Typography>
                                        </Grid>
                                        <Grid item sm zeroMinWidth>
                                            {totals ? (
                                                <Typography variant="h4">{(totals?.pError !== 'NaN%' && totals.pError) || 0}</Typography>
                                            ) : (
                                                <Skeleton variant="rectangular" width={100} />
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </>
                        )}

                        <Grid item xs zeroMinWidth />
                    </Grid>
                </Grid>
            </Box>
            <Box>
                {!series && <Skeleton variant="rectangular" height={320} />}
                {series && <Chart {...chartData} />}
            </Box>
        </MainCard>
    );
};
export default IntegrationEventsAreaChartCard;
